import React, {useEffect, useMemo, useRef, useState} from "react";
import {CoreDataType} from "../../../../../common/apis/datatype/DataTypeDefinitionDto";
import {PropertyDefinitionDto, PropertyDto} from "../../../../../common/apis/property/PropertyApi";
import {AttributeDefinitionDto} from "../../../../../common/apis/stereotype/AttributeDefinitionDto";
import {CancelButton} from "../../../../../components/button/CancelButton";
import {SaveButton} from "../../../../../components/button/SaveButton";
import Dialog from "../../../../../components/dialogs/Dialog";
import DialogActions from "../../../../../components/dialogs/DialogActions";
import DialogContent from "../../../../../components/dialogs/DialogContent";
import DialogTitle from "../../../../../components/dialogs/DialogTitle";
import Grid from "../../../../../components/dialogs/Grid";
import SingleSelectField from "../../../../../components/fields/select/SingleSelectField";
import TextField from "../../../../../components/fields/textfield/TextField";
import {t} from "../../../../../store/localization/TranslMessasge";
import {CommonTranslation} from "../../CommonTranslation";
import {CoreDataTypeFormatter} from "../../datatype/CoreDataTypeFormatter";
import {ErrorTranslationKey} from "../../ErrorTranslationKey";
import Snackbar from "../../snackbar/Snackbar";
import {AttributeValueValidator} from "../../stereotypes/attributes/AttributeValueValidator";
import {PropertiesTranslationKey} from "../PropertiesTranslationKey";
import {PropertyDefinitionIdConverter} from "../PropertyDefinitionIdConverter";
import {PropertyValidator} from "../PropertyValidator";


export interface PropertyCreateApi {
    createProperty: (name: string, type: string, value: string, propertyDefinitionId?: string) => Promise<PropertyDto>;
    getExistingPropertyDefinitions: () => PropertyDefinitionDto[];
    refreshExistingPropertyDefinitions: () => void;
    findAllAttributeDefinitions: () => Promise<AttributeDefinitionDto[]>;
}

export interface PropertyCreateDialogProps {
    open: boolean;
    onCreated: () => void;
    onClosed: () => void;
    existingPropertyNames: Set<string>;
    propertyCreateApi: PropertyCreateApi;
}

export default function PropertyCreateDialog(props: Readonly<PropertyCreateDialogProps>) {
    const validator = useRef(new PropertyValidator());
    const valueValidator = useRef(new AttributeValueValidator());
    const {open, onCreated, onClosed, existingPropertyNames, propertyCreateApi} = props

    const [propertyDefinitionIdentifier, setPropertyDefinitionIdentifier] = useState<string | undefined>(undefined);
    const [attributeDefinitions, setAttributeDefinitions] = useState<AttributeDefinitionDto[]>([]);
    const [name, setName] = useState<string>("");
    const [type, setType] = useState<string>(CoreDataType.STRING);
    const [value, setValue] = useState<string>("");

    const [validated, setValidated] = useState<boolean>(false);
    const [nameValidationMsg, setNameValidationMsg] = useState<string | undefined>(undefined);
    const [typeValidationMsg, setTypeValidationMsg] = useState<string | undefined>(undefined);
    const [valueValidationMsg, setValueValidationMsg] = useState<string | undefined>(undefined);

    const propertyDefinitions = propertyCreateApi.getExistingPropertyDefinitions();
    const propertyNames = propertyDefinitions.map(propertyDefinition => propertyDefinition.name);

    const coreDataTypes = useMemo(() => {
        const types = Object.values(CoreDataType);
        types.sort((a, b) => CoreDataTypeFormatter.formatCoreDataType(a, t).localeCompare(CoreDataTypeFormatter.formatCoreDataType(b, t)));
        return types;
    }, []);

    useEffect(() => {
        propertyCreateApi.findAllAttributeDefinitions()
            .then(attributeDefinitions => setAttributeDefinitions(attributeDefinitions));
    }, [propertyCreateApi]);

    useEffect(() => {
        return () => {
            if (open) {
                setName("");
                setNameValidationMsg(undefined);
                setValue("");
                setType(CoreDataType.STRING);
                setTypeValidationMsg(undefined);
            }
        }
    }, [open]);

    function closeDialog() {
        onClosed();
    }

    function updateName(newValue: string | null) {
        const propertyDefinition = findPropertyDefinitionByName(newValue);
        if (propertyDefinition) {
            setPropertyDefinitionIdentifier(propertyDefinition.identifier);
            setName(propertyDefinition.name);
            setType(propertyDefinition.type);
        } else {
            setName(newValue ?? "");
            setPropertyDefinitionIdentifier(undefined);
        }
    }

    function findPropertyDefinitionByName(name: string | undefined | null) {
        if (name) {
            return propertyDefinitions.find(propertyDefinition => propertyDefinition.name === name);
        } else {
            return undefined;
        }
    }

    function validateName(name: string): boolean {
        let isValid = validator.current.validateName(name);
        setNameValidationMsg(isValid ? undefined : t(CommonTranslation.FILL_OUT_THIS_FIELD));
        if (isValid) {
            if (existingPropertyNames.has(name)) {
                setNameValidationMsg(t(ErrorTranslationKey.FAILED_TO_ADD_DUPLICATE_ATTRIBUTE));
                isValid = false;
            }
        }
        return isValid;
    }

    function validateValue(text: string): boolean {
        const attributeDefinition = findAttributeDefinition(propertyDefinitionIdentifier);
        if (attributeDefinition !== undefined) {
            const errors = valueValidator.current.validateValue(text, attributeDefinition, t);
            if (errors[attributeDefinition.id]) {
                setValueValidationMsg(errors[attributeDefinition.id]);
                return false;
            } else {
                return true;
            }
        } else {
            const isValid = validator.current.validateValue(text);
            setValueValidationMsg(isValid ? undefined : t(CommonTranslation.FILL_OUT_THIS_FIELD));
            return isValid;
        }
    }

    function findAttributeDefinition(propertyDefinitionIdentifier: string | undefined): AttributeDefinitionDto | undefined {
        if (propertyDefinitionIdentifier === undefined) {
            return undefined;
        }
        const attributeDefId = PropertyDefinitionIdConverter.convertToAttrDefId(propertyDefinitionIdentifier);
        return attributeDefinitions.find(attributeDefinition => attributeDefinition.id === attributeDefId);
    }

    function validateType(text: string): boolean {
        const isValid = validator.current.validateType(text);
        setTypeValidationMsg(isValid ? undefined : t(CommonTranslation.FILL_OUT_THIS_FIELD));
        return isValid;
    }

    function isFormValid() {
        const isNameValid = validateName(name);
        const isTypeValid = validateType(type);
        const isValueValid = validateValue(value);

        return isNameValid && isTypeValid && isValueValid;
    }

    const createProperty = () => {
        setValidated(true)

        if (isFormValid()) {
            propertyCreateApi.createProperty(name.trim(), type, value, propertyDefinitionIdentifier)
                .then(() => {
                    propertyCreateApi.refreshExistingPropertyDefinitions();
                    onCreated();
                    closeDialog();
                    Snackbar.success(t(PropertiesTranslationKey.PROPERTY_CREATED_SUCCESSFULLY));
                })
                .catch((error) => Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error));
        }
    }

    if (!propertyDefinitions) {
        return null;
    }

    return (
        <Dialog open={open}
                onClose={closeDialog}
                fullWidth={true}>

            <DialogTitle id="property-create-dialog"
                         title={t(PropertiesTranslationKey.NEW_PROPERTY)}
                         onDialogClosed={closeDialog}/>

            <DialogContent>
                <Grid container>
                    <Grid item xs={12}>
                        <SingleSelectField
                                id="name-field"
                                label={t(PropertiesTranslationKey.NAME)}
                                value={name}
                                onChange={(newValue: string | null) => updateName(newValue)}
                                options={propertyNames}
                                getOptionLabel={option => option}
                                acceptFreeText={true}
                                required={true}
                                errorMessage={nameValidationMsg}/>
                    </Grid>
                    <Grid item xs={12}>
                        <SingleSelectField
                                id={"type-field"}
                                label={t(PropertiesTranslationKey.TYPE)}
                                value={type}
                                onChange={(type) => {
                                    if (validated) {
                                        validateType(type ?? "");
                                    }
                                    setType(type ?? "");
                                }}
                                options={coreDataTypes}
                                getOptionLabel={coreDataType => CoreDataTypeFormatter.formatCoreDataType(coreDataType, t)}
                                required={true}
                                disabled={propertyDefinitionIdentifier !== undefined}
                                errorMessage={typeValidationMsg}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField id="value-field"
                                   label={t(PropertiesTranslationKey.VALUE)}
                                   value={value}
                                   required={true}
                                   errorMessage={valueValidationMsg}
                                   onChange={(value) => {
                                       if (validated) {
                                           validateValue(value);
                                       }
                                       setValue(value);
                                   }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <SaveButton onClick={createProperty}/>
                <CancelButton onClick={closeDialog} variant={"outlined"}/>
            </DialogActions>
        </Dialog>
    );
}
